// Reconnect + foreground drain trigger (offline-resilience substrate).
//
// drainPending() exists in src/lib/native/offlineQueue.ts but nothing invokes it on reconnect/resume,
// so 'failed' captures never auto-retry. This hook wires that trigger ONCE (mounted in MobileShell):
//   • @capacitor/network 'networkStatusChange' -> on reconnect, refetch React Query + run the drain;
//   • @capacitor/app 'resume'                  -> on foreground, refetch React Query + run the drain;
//   • web: window 'online' -> refetch + run the drain (queue drain is a no-op shape on web but safe).
//
// Mirrors the exact dynamic-import + addListener + disposer pattern from useNotifications. All native
// access is dynamic-imported + try/catch so the web bundle never hard-depends on the plugins. The
// caller supplies the drain callback (the capture slice owns the actual upload handler) — when omitted
// this still refetches active queries so cached views refresh on reconnect.
//
// WS-LINK: 'resume' is also the hook point for native→WebView session reconciliation (plan §5, g3
// §4 G1) — on foreground, re-read the on-disk session and adopt it if a native process (e.g. Android
// Auto) rotated the token while the WebView was backgrounded. Runs BEFORE the query refetch/drain so
// any auth-gated refetch uses the reconciled session. Reuses this existing resume listener rather than
// adding a second `App.addListener('resume', ...)`, per plan §5.

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isNative } from '@/lib/native/platform';
import { reconcileSessionOnResume } from '@/lib/native/sessionBridge';

interface UseReconnectDrainOptions {
  /**
   * Optional drain callback invoked on every reconnect/resume after queries are refetched. The capture
   * slice passes a function that calls drainPending(handler); when omitted only the refetch happens.
   * Must never throw (errors are swallowed defensively).
   */
  onReconnect?: () => Promise<void> | void;
}

export function useReconnectDrain(options: UseReconnectDrainOptions = {}): void {
  const { onReconnect } = options;
  const qc = useQueryClient();

  useEffect(() => {
    let disposed = false;
    const disposers: Array<() => void> = [];

    const fire = async () => {
      if (disposed) return;
      // Refresh cached/active reads so the UI catches up on reconnect.
      try {
        await qc.refetchQueries({ type: 'active' });
      } catch {
        /* refetch best-effort */
      }
      if (onReconnect) {
        try {
          await onReconnect();
        } catch {
          /* drain callback must not break the trigger */
        }
      }
    };

    if (isNative()) {
      (async () => {
        try {
          const { Network } = await import('@capacitor/network');
          const netSub = await Network.addListener('networkStatusChange', (s) => {
            if (s.connected) void fire();
          });
          disposers.push(() => {
            void netSub.remove();
          });
        } catch {
          /* plugin unavailable: degrade silently */
        }

        try {
          const { App } = await import('@capacitor/app');
          const appSub = await App.addListener('resume', () => {
            void (async () => {
              // Reconcile the session BEFORE refetching/draining so any auth-gated request that
              // follows uses a session that reflects a native-side rotation, not a stale in-memory one.
              await reconcileSessionOnResume();
              await fire();
            })();
          });
          disposers.push(() => {
            void appSub.remove();
          });
        } catch {
          /* plugin unavailable: degrade silently */
        }
      })();
    } else if (typeof window !== 'undefined') {
      const onOnline = () => void fire();
      window.addEventListener('online', onOnline);
      disposers.push(() => window.removeEventListener('online', onOnline));
    }

    return () => {
      disposed = true;
      for (const d of disposers) d();
    };
    // qc is stable; onReconnect identity is the caller's responsibility (wrap in useCallback if needed).
  }, [qc, onReconnect]);
}
